import { useEffect, useState } from "react";
import { CalendarClock, ArrowRight } from "lucide-react";
import { CAL_COM_LINK, CAL_NAMESPACE, CAL_COM_URL, loadCalCom } from "@/lib/scheduling";

/**
 * Book-a-call button — opens the Cal.com popup on click.
 * Falls back to opening cal.com/go-higher in a new tab if the script fails.
 */
export default function BookCallButton({
  className = "",
  variant = "primary",
  label = "Book a free call",
  showIcon = true,
  testId = "book-call-button",
}) {
  const [ready, setReady] = useState(
    typeof window !== "undefined" && !!window.Cal?.ns?.[CAL_NAMESPACE]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadCalCom();
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openPopup = (e) => {
    e.preventDefault();
    if (typeof window !== "undefined" && window.Cal?.ns?.[CAL_NAMESPACE]) {
      window.Cal.ns[CAL_NAMESPACE]("modal", {
        calLink: CAL_COM_LINK,
        config: { layout: "month_view", theme: "dark" },
      });
    } else {
      window.open(CAL_COM_URL, "_blank", "noopener,noreferrer");
    }
  };

  const base =
    "group inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full font-semibold text-sm transition-colors";
  const styles = {
    primary:
      "bg-[#10B981] hover:bg-[#0ea774] text-white shadow-lg shadow-emerald-500/25",
    secondary:
      "border border-white/15 hover:border-white/30 bg-white/[0.02] hover:bg-white/[0.05] text-white",
    gold: "bg-[#D4AF37] hover:bg-[#c39c26] text-[#0A0F1C] shadow-lg shadow-[#D4AF37]/25",
    ghost: "text-white/80 hover:text-white",
  };

  return (
    <button
      type="button"
      onClick={openPopup}
      data-testid={testId}
      aria-label={label}
      className={`${base} ${styles[variant] || styles.primary} ${className}`}
    >
      {showIcon && <CalendarClock className="w-4 h-4" />}
      <span>{label}</span>
      {variant === "primary" && (
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      )}
      <span className="sr-only">{ready ? "Scheduler ready" : "Loading scheduler…"}</span>
    </button>
  );
}
